import t from '@rcm/i18n-client';
import { Button } from 'forms';
import React, { useCallback } from 'react';
import getCreditCardData from '../../functions/getCreditCardData';
import useCreditCardFieldsState from '../../hooks/useCreditCardFieldsState';
import type { CreditCardData, LegacyCartFormMessage } from '../../types';
import CreditCardFields from '../credit-card/CreditCardFields';
import CreditCardForm from '../credit-card/CreditCardForm';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCartPaymentInfo,
  selectCartInfo,
  selectIsEditing,
} from 'cart-ui-react/src/autoshipState/selectors';
import CartV2CreditCardFields from '/cart-ui-react/src/container/CartV2/components/CartV2CreditCardFields';
import { getCheckoutOrderManager } from 'cart-ui';
import {
  setEditingAlert,
  setSnackbar,
} from 'cart-ui-react/src/autoshipState/autoshipSlice';
interface Props {
  order: any;
  onSubmit?: (data: { type: string; data: CreditCardData }) => void;
  messages: LegacyCartFormMessage[];
  paymentType: string;
  disabled?: boolean;
  isCartV2?: boolean;
  setError?: any;
}

const PaymentFormCreditCardPaynamics3ds = ({
  order,
  onSubmit,
  messages,
  paymentType,
  disabled,
  isCartV2 = false,
  setError,
}: Props) => {
  const creditCardFieldsState = useCreditCardFieldsState();
  const cartV2PaymentInfo = useSelector(selectCartPaymentInfo);
  const paymentInfo = isCartV2
    ? cartV2PaymentInfo
    : getCreditCardData(creditCardFieldsState);
  const cartV2Info = useSelector(selectCartInfo);
  const dispatch = useDispatch();
  const isEditing = useSelector(selectIsEditing);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (disabled || isEditing) return;
      onSubmit({
        data: getCreditCardData(creditCardFieldsState),
        type: paymentType,
      });
    },
    [creditCardFieldsState, onSubmit, paymentType]
  );

  const handleEditingAlert = () => {
    if (isCartV2 && isEditing) {
      dispatch(setEditingAlert(true));
      dispatch(
        setSnackbar({
          message: t('Please save any changes before you proceed.'),
          type: 'warning',
          delay: 4500,
        })
      );
      setTimeout(() => {
        dispatch(setEditingAlert(false));
      }, 100);
      return;
    }
  };

  const handleSubmitV2 = useCallback(
    (event) => {
      event.preventDefault();
      if (disabled || isEditing) return;
      getCheckoutOrderManager().then(async (orderManager) => {
        const payment = {
          cardNumber: paymentInfo.cardNumber,
          nameOnCard: paymentInfo.nameOnCard,
          expirationMonth: Number(paymentInfo.expirationMonth),
          expirationYear: Number(paymentInfo.expirationYear),
          verificationCode: paymentInfo.verificationCode,
        };

        // check for cart changes (mainly Kalogris donations but any frontend cart changes would get applied here)
        await orderManager.patchOrder(cartV2Info);
        await orderManager
          .processCurrentOrder({ type: paymentType, data: payment })
          .then(() => {
            const paymentError = JSON.parse(
              localStorage.getItem('paymentError')
            );
            if (paymentError) {
              dispatch(setError(paymentError[0].value));
              localStorage.removeItem('paymentError');
            }
          });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSubmit, paymentType, paymentInfo]
  );

  return (
    <div className='paynamics3dsPaymentFormWrapper'>
      <CreditCardForm
        onSubmit={isCartV2 ? handleSubmitV2 : handleSubmit}
        cartV2={isCartV2}
      >
        <CreditCardForm.Body>
          {isCartV2 ? (
            <CartV2CreditCardFields />
          ) : (
            <CreditCardFields
              {...creditCardFieldsState}
              disabled={disabled}
              messages={messages}
              order={order}
            />
          )}
        </CreditCardForm.Body>
        <CreditCardForm.Foot>
          {disabled || isEditing ?
            <Button
              css={{ opacity: '0.7' }}
              btn='primary'
              className='continueButton'
              onClick={handleEditingAlert}
              text={t('Place Order')}
            />
          :
            <div>
              <Button
                btn='primary'
                className='continueButton'
                disabled={disabled || isEditing}
                text={t('Place Order')}
                type='submit'
              />
            </div>
          }
        </CreditCardForm.Foot>
      </CreditCardForm>
    </div>
  );
};

export default PaymentFormCreditCardPaynamics3ds;
